import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, ArrowUpRight, Eye, X } from "lucide-react";
import { Link } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import { IMAGES } from "@/data/content";

const spans = [
  "md:col-span-2 md:row-span-2",
  "md:col-span-1 md:row-span-1",
  "md:col-span-1 md:row-span-1",
  "md:col-span-1 md:row-span-1",
  "md:col-span-1 md:row-span-1",
  "md:col-span-1 md:row-span-1",
  "md:col-span-1 md:row-span-1",
  "md:col-span-1 md:row-span-1",
  "md:col-span-1 md:row-span-1",
];

const coverOrder = [
  "lirica-concept",
  "jac-refine-m5-2012",
  "fiat-idea-cinque-terre-2004",
  "ducato-van",
  "roller-team-pegaso-gtx",
  "rc-chassis",
  "infradito-1995",
  "trio",
  "iveco-stralis-2005",
];

const portfolioItems = coverOrder.map((id) => IMAGES.portfolio.find((item) => item.id === id));

export const Portfolio = () => {
  const { t } = useLang();
  const [activeIndex, setActiveIndex] = useState(null);
  const activeImage = activeIndex === null ? null : portfolioItems[activeIndex];

  const closeLightbox = () => setActiveIndex(null);
  const showPrevious = () =>
    setActiveIndex((index) => (index - 1 + portfolioItems.length) % portfolioItems.length);
  const showNext = () => setActiveIndex((index) => (index + 1) % portfolioItems.length);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (activeIndex === null) return;
      if (event.key === "Escape") setActiveIndex(null);
      if (event.key === "ArrowLeft") {
        setActiveIndex((index) => (index - 1 + portfolioItems.length) % portfolioItems.length);
      }
      if (event.key === "ArrowRight") {
        setActiveIndex((index) => (index + 1) % portfolioItems.length);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    document.body.style.overflow = activeIndex === null ? "" : "hidden";
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "";
    };
  }, [activeIndex]);

  return (
    <section
      id="portfolio"
      data-testid="portfolio-section"
      className="relative bg-[#050507] py-24 md:py-32 lg:py-40"
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-12">
        <div className="max-w-2xl">
          <p className="font-mono text-xs tracking-[0.2em] uppercase font-bold text-zinc-500">
            {t.portfolio.overline}
          </p>
          <h2 className="mt-5 font-heading text-3xl sm:text-4xl tracking-tight font-medium text-white">
            {t.portfolio.title}
          </h2>
          <p
            data-testid="portfolio-confidentiality-note"
            className="mt-5 max-w-3xl border-l border-white/15 pl-4 text-sm leading-relaxed text-zinc-400"
          >
            {t.portfolio.confidentiality}
          </p>
          <p className="mt-4 text-base leading-relaxed text-zinc-400">{t.portfolio.subtitle}</p>
        </div>

        <motion.div
          data-testid="portfolio-manifesto-intro"
          initial={{ opacity: 0, x: -28 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, amount: 0.5 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="mt-14 flex flex-col gap-4 border-l border-[#00F0FF] pl-5 md:mt-16 md:pl-7"
        >
          <span className="font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-[#00F0FF]">
            {t.portfolio.manifestoLabel}
          </span>
          <p
            data-testid="portfolio-manifesto-phrase"
            className="max-w-3xl font-heading text-2xl font-medium leading-tight tracking-tight text-white sm:text-3xl lg:text-4xl"
          >
            {t.portfolio.manifestoLead}
          </p>
        </motion.div>

        <div className="mt-14 grid grid-cols-1 gap-4 md:grid-cols-3 md:auto-rows-[260px]">
          {portfolioItems.map((item, index) => (
            <motion.div
              key={item.id}
              data-testid={`portfolio-item-${item.id}`}
              initial={{ opacity: 0, scale: 0.97 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1], delay: index * 0.08 }}
              className={`group relative overflow-hidden rounded-xl border border-white/5 ${spans[index]}`}
            >
              <img
                src={item.src}
                alt={item.client}
                style={{ objectPosition: item.objectPosition }}
                className="absolute inset-0 h-full w-full object-cover transition-transform duration-1000 ease-out group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/10 to-transparent opacity-90" />
              <div className="absolute inset-0 rounded-xl ring-0 ring-inset ring-[#00F0FF]/40 transition-all duration-500 group-hover:ring-1" />
              <Link
                to={`/project/${item.id}`}
                data-testid={`portfolio-project-link-${item.id}`}
                aria-label={`${t.portfolio.openProject}: ${item.client}`}
                className="absolute inset-0 z-10"
              />
              <button
                type="button"
                onClick={() => setActiveIndex(portfolioItems.findIndex((project) => project.id === item.id))}
                data-testid={`portfolio-lightbox-open-${item.id}`}
                aria-label={`${t.portfolio.viewImage}: ${item.client}`}
                title={t.portfolio.viewImage}
                className="absolute right-4 top-4 z-20 inline-flex h-10 w-10 items-center justify-center border border-white/20 bg-black/35 text-white transition-colors duration-300 hover:border-[#00F0FF] hover:text-[#00F0FF] md:opacity-0 md:group-hover:opacity-100 md:group-focus-within:opacity-100"
              >
                <Eye className="h-4 w-4" aria-hidden="true" />
              </button>
              <div className="pointer-events-none relative z-10 flex h-full flex-col justify-end p-6">
                <div className="mb-auto flex justify-end opacity-0 transition-opacity duration-300 group-hover:opacity-100 group-focus-within:opacity-100">
                  <span className="inline-flex h-9 w-9 items-center justify-center border border-white/20 bg-black/30 text-white">
                    <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
                  </span>
                </div>
                <span className={`font-mono text-[10px] tracking-[0.2em] text-[#00F0FF] ${item.id === "rc-chassis" ? "" : "uppercase"}`}>
                  {item.tag}
                </span>
                <div className="mt-1 flex items-end justify-between gap-4">
                  <h3 className="font-heading text-lg font-medium text-white">{item.client}</h3>
                  <span className="shrink-0 font-mono text-[10px] uppercase tracking-[0.16em] text-zinc-300">
                    {t.portfolio.openProject}
                  </span>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {activeImage &&
        createPortal(
          <motion.div
            data-testid="portfolio-lightbox"
            role="dialog"
            aria-modal="true"
            aria-label={activeImage.client}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-[100] flex bg-black/95 p-4 backdrop-blur-sm sm:p-8"
            onClick={closeLightbox}
          >
            <div
              className="relative flex h-full w-full flex-col"
              onClick={(event) => event.stopPropagation()}
            >
              <div className="flex items-center justify-between gap-6">
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#00F0FF]">
                    {activeImage.tag}
                  </p>
                  <p className="mt-1 font-heading text-base font-medium text-white sm:text-lg">
                    {activeImage.client}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    closeLightbox();
                  }}
                  data-testid="portfolio-lightbox-close"
                  aria-label={t.portfolio.closeLightbox}
                  title={t.portfolio.closeLightbox}
                  className="inline-flex h-11 w-11 shrink-0 items-center justify-center border border-white/15 text-white transition-colors duration-300 hover:border-[#00F0FF] hover:text-[#00F0FF]"
                >
                  <X className="h-5 w-5" aria-hidden="true" />
                </button>
              </div>

              <div className="relative my-4 min-h-0 flex-1 sm:my-6">
                <motion.img
                  key={activeImage.id}
                  initial={{ opacity: 0, scale: 0.985 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.35 }}
                  src={activeImage.src}
                  alt={activeImage.client}
                  data-testid="portfolio-lightbox-image"
                  className="h-full w-full object-contain"
                />
              </div>

              <div className="flex items-center justify-between gap-5 border-t border-white/10 pt-4 sm:pt-6">
                <button
                  type="button"
                  onClick={showPrevious}
                  data-testid="portfolio-lightbox-previous"
                  aria-label={t.portfolio.previousImage}
                  title={t.portfolio.previousImage}
                  className="inline-flex h-11 w-11 items-center justify-center border border-white/15 text-white transition-colors duration-300 hover:border-[#00F0FF] hover:text-[#00F0FF]"
                >
                  <ArrowLeft className="h-5 w-5" aria-hidden="true" />
                </button>
                <p data-testid="portfolio-lightbox-counter" className="font-mono text-[10px] uppercase tracking-[0.18em] text-zinc-500">
                  {t.portfolio.imageCounter
                    .replace("{current}", activeIndex + 1)
                    .replace("{total}", portfolioItems.length)}
                </p>
                <button
                  type="button"
                  onClick={showNext}
                  data-testid="portfolio-lightbox-next"
                  aria-label={t.portfolio.nextImage}
                  title={t.portfolio.nextImage}
                  className="inline-flex h-11 w-11 items-center justify-center border border-white/15 text-white transition-colors duration-300 hover:border-[#00F0FF] hover:text-[#00F0FF]"
                >
                  <ArrowRight className="h-5 w-5" aria-hidden="true" />
                </button>
              </div>
            </div>
          </motion.div>,
          document.body,
        )}
    </section>
  );
};